import React, { ReactNode } from 'react';
import { Box, IconButton, SvgIcon } from '@mui/material';

import getMuiIcon from '../../../../utils/getMuiIcon';
import useScreenSize from '../../../../utils/useScreenSize';
import { customSizes, palette, spaces } from '../../../../assets/styles/themes/tokens';
import MuLogo from '../../SideNavbar/MuLogo';
import { colors } from '../../../../assets/styles/themes/variables';

interface TopBarProps {
  onHomeClick: () => void;
  onBurgerMenuClick: () => void;
  content?: ReactNode;
}

const getContainerStyles = (isLargeScreen: boolean) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  height: isLargeScreen
    ? customSizes.sideNavbar.headerHeight.desktop : customSizes.sideNavbar.headerHeight.mobile,
  boxSizing: 'content-box',
  borderBottom: `1px solid ${palette.elevation.outlined}`,
  overflow: 'hidden',
  backgroundColor: colors.neutral0,
});

const getLogoBoxStyles = (isSmallScreen: boolean) => ({
  width: isSmallScreen ? '166px' : customSizes.sideNavbar.collapsedWidth,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  ...(!isSmallScreen && {
    borderRight: `1px solid ${palette.elevation.outlined}`,
  }),
  height: '100%',
});

const burgerMenuStyles = {
  borderLeft: `1px solid ${palette.elevation.outlined}`,
  width: '56px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const iconButtonStyles = {
  padding: spaces.md,
  borderRadius: 0,
  color: palette.text.secondary,
  '&:focus': {
    backgroundColor: palette.primary.selected,
  },
};

const getContentContainerStyles = (isLargeScreen: boolean) => ({
  flex: 1,
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  padding: `${spaces.sm} ${isLargeScreen ? spaces.md : spaces.sm}`,
});

const TopBar: React.FC<TopBarProps> =
({ onHomeClick, onBurgerMenuClick, content }) => {
  const { isXsScreen, isMdScreen, isLgScreen } = useScreenSize();
  const isDesktopScreen = isMdScreen || isLgScreen;

  const containerStyles = getContainerStyles(isDesktopScreen);
  const logoBoxStyles = getLogoBoxStyles(isXsScreen);
  const contentContainerStyles = getContentContainerStyles(isDesktopScreen);

  if (isDesktopScreen) {
    return (
      <Box
        sx={containerStyles}
        role='banner'
        data-testid='top-bar'
      >
        <Box sx={contentContainerStyles}>
          {content}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={containerStyles} data-testid='top-bar'>
      <Box
        sx={logoBoxStyles}
      >
        <MuLogo variant={isXsScreen ? 'full' : 'small'} onClick={onHomeClick} sx={{
          height: '100%',
          ...(!isXsScreen && {
            paddingX: spaces.sm,
            paddingY: spaces.md,
          }),
        }} />
      </Box>
      <Box sx={contentContainerStyles}>
        {content}
      </Box>
      <Box sx={burgerMenuStyles}>
        <IconButton
          onClick={onBurgerMenuClick}
          data-testid='burger-menu-button'
          sx={iconButtonStyles}
        >
          <SvgIcon
            component={getMuiIcon('hamburger')}
          />
        </IconButton>
      </Box>
    </Box>
  );
};

export default TopBar;
